import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Role, RolePermission } from '@/types/roles';
import { toast } from 'sonner';

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .order('is_system', { ascending: false })
        .order('name');
      
      if (error) throw error;
      return data as Role[];
    },
  });
}

export function useRolePermissions(roleId: string | null) {
  return useQuery({
    queryKey: ['role-permissions', roleId],
    queryFn: async () => {
      if (!roleId) return [];
      const { data, error } = await supabase
        .from('role_permissions')
        .select('*')
        .eq('role_id', roleId);
      
      if (error) throw error;
      return data as RolePermission[];
    },
    enabled: !!roleId,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (role: Partial<Role>) => {
      // Get current user's session
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      // Get the current user's profile to get company_id and profile id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', user.id)
        .single();
      
      if (profileError || !profile) throw new Error('No profile found');
      
      // Use DB function for unique slug generation (tenant derived from auth context)
      const { data: slugData, error: slugError } = await supabase
        .rpc('generate_unique_role_slug', {
          p_name: role.name || ''
        });
      
      if (slugError) throw slugError;
      
      const { data, error } = await supabase
        .from('roles')
        .insert([{
          name: role.name || '',
          description: role.description,
          is_default: role.is_default ?? false,
          requires_2fa: role.requires_2fa ?? false,
          primary_manager_role_id: role.primary_manager_role_id,
          company_id: profile.company_id,
          slug: slugData,
          created_by: profile.id,
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Role created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create role: ' + error.message);
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Role> & { id: string }) => {
      const { data, error } = await supabase
        .from('roles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Role updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update role: ' + error.message);
    },
  });
}

export function useUpdateRolePermission() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ roleId, permissionKey, enabled }: { roleId: string; permissionKey: string; enabled: boolean }) => {
      // Try UPDATE first (expected path — row seeded by trigger)
      const { data, error } = await supabase
        .from('role_permissions')
        .update({ enabled, updated_at: new Date().toISOString() })
        .eq('role_id', roleId)
        .eq('permission_key', permissionKey)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      
      // Row existed and was updated — done
      if (data) return data;
      
      // Row missing (catalog drift) — recover via upsert
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (!profile?.company_id) throw new Error('No company found');
      
      const { data: upserted, error: upsertError } = await supabase
        .from('role_permissions')
        .upsert({
          role_id: roleId,
          permission_key: permissionKey,
          company_id: profile.company_id,
          enabled,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'role_id,permission_key' })
        .select()
        .single();
      
      if (upsertError) throw upsertError;
      return upserted;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions', variables.roleId] });
    },
  });
}

export function useDeleteRoleWithReassignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ roleId, replacementRoleId }: { roleId: string; replacementRoleId?: string }) => {
      // Use atomic RPC for delete with reassignment
      const { data, error } = await supabase
        .rpc('delete_role_with_reassignment', {
          p_role_id: roleId,
          p_replacement_role_id: replacementRoleId || null
        });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['role-user-count'] });
      toast.success('Role deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete role: ' + error.message);
    },
  });
}

// Keep legacy hook for backwards compatibility
export function useDeleteRole() {
  const deleteWithReassignment = useDeleteRoleWithReassignment();
  
  return {
    ...deleteWithReassignment,
    mutateAsync: async (id: string) => {
      return deleteWithReassignment.mutateAsync({ roleId: id });
    }
  };
}

export function useRoleUserCount(roleId: string | null) {
  return useQuery({
    queryKey: ['role-user-count', roleId],
    queryFn: async () => {
      if (!roleId) return 0;
      const { count, error } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role_id', roleId);
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !!roleId,
  });
}
